import { Link } from 'react-router-dom';
import type { ServerDoc, ServerLiveStats } from '@panel/shared';
import StatusPill from './StatusPill.js';

interface Props {
  server: ServerDoc;
  live: ServerLiveStats | undefined;
  nodeOnline: boolean;
}

export default function ServerCard({ server, live, nodeOnline }: Props) {
  const status = !nodeOnline ? 'offline' : live?.status ?? 'offline';
  // When the machine's agent is unreachable, any cached rcon stats are stale.
  // Drop them so the card doesn't show "live" player counts that the agent
  // hasn't actually confirmed in 60+ seconds.
  const rcon = nodeOnline ? live?.rcon : undefined;
  const isOnline = status === 'online';

  return (
    <Link
      to={`/servers/${server.Server_ID}`}
      className={`card card-hover block p-4 group cursor-pointer ${
        isOnline ? 'hover:border-accent/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-fg truncate group-hover:text-accent transition-colors">
            {server.Server_Name}
          </h3>
          <p className="text-xs text-fg-muted truncate mt-0.5 flex items-center gap-1.5">
            <span className="text-fg-dim font-mono text-[10px]">#{server.Server_ID}</span>
            <span className="text-fg-dim">·</span>
            <span className="truncate">{server.Server_Machine}</span>
            <span className="text-fg-dim">·</span>
            <span className="truncate">{server.Server_Group}</span>
          </p>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-bg-border">
        <Mini label="Players" value={rcon ? `${rcon.Players ?? 0}/${rcon.MaxPlayers ?? '—'}` : '—'} />
        <Mini label="FPS" value={rcon?.Framerate != null ? String(rcon.Framerate) : '—'} />
        <Mini label="Entities" value={rcon ? formatNum(rcon.EntityCount) : '—'} />
      </div>
    </Link>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-fg-dim tracking-wider font-semibold">{label}</div>
      <div className="text-sm font-medium text-fg tabular-nums truncate mt-0.5">{value}</div>
    </div>
  );
}

function formatNum(n: number | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}
